import { Link } from "react-router-dom";
import { ArrowRight, ShieldCheck, Award, Heart, Activity, Building2, MapPin, Phone, GraduationCap } from "lucide-react";
import HeartbeatLine from "@/components/HeartbeatLine";
import SEO, { ORGANIZATION_SCHEMA, LOCATIONS_SCHEMA, faqPageSchema } from "@/components/SEO";
import { COURSES, STATS, SEDI, CLIENTS, FAQ_HOME, HERO_IMAGE, COMPANY, coursesByCategory, CATEGORIES } from "@/lib/data";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";

export default function Home() {
    return (
        <>
            <SEO
                title="Corsi Primo Soccorso e BLS-AED in Ticino"
                description="7MED · Centro certificato SRC ed EduQua. Corsi BLS-AED per aziende (OLL3), Corso Samaritani per la patente. Noleggio defibrillatori AED. 4 sedi in Ticino: Mendrisio, Lugano-Savosa, Bellinzona, Locarno."
                path="/"
                keywords="corso BLS-AED Ticino, corso primo soccorso aziende OLL3, corso samaritani patente Ticino, noleggio defibrillatore AED Svizzera, formazione SRC EduQua"
                ogImage={HERO_IMAGE}
                structuredData={[
                    ORGANIZATION_SCHEMA,
                    ...LOCATIONS_SCHEMA,
                    {
                        "@context": "https://schema.org",
                        "@type": "WebSite",
                        "@id": "https://7med.ch/#website",
                        url: "https://7med.ch",
                        name: "7MED",
                        alternateName: "7MED GROUP",
                        description: "Corsi BLS-AED, Primo Soccorso e noleggio defibrillatori in Ticino.",
                        inLanguage: "it-CH",
                        publisher: { "@id": "https://7med.ch/#organization" },
                        potentialAction: {
                            "@type": "SearchAction",
                            target: { "@type": "EntryPoint", urlTemplate: "https://7med.ch/blog?q={search_term_string}" },
                            "query-input": "required name=search_term_string",
                        },
                    },
                    faqPageSchema(FAQ_HOME),
                ]}
            />
            {/* HERO */}
            <section className="relative overflow-hidden bg-white" data-testid="home-hero">
                <div className="absolute inset-0 grid-line opacity-50 pointer-events-none" />
                <div className="container-7m pt-12 lg:pt-20 pb-12 lg:pb-24 relative">
                    <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-end">
                        <div className="lg:col-span-7">
                            <div className="flex items-center gap-3 mb-6">
                                <span className="w-2 h-2 bg-brand rounded-full animate-pulse-dot" />
                                <span className="label-eyebrow">Corsi certificati in Ticino</span>
                            </div>
                            <h1 className="font-heading font-black tracking-tighter leading-[1.02] sm:leading-[0.95] text-[44px] sm:text-6xl lg:text-8xl">
                                La formazione<br /><span className="text-brand">che fa</span> la differenza.
                            </h1>
                            <p className="mt-6 sm:mt-8 text-readable-lg max-w-xl">
                                Corsi certificati per <strong className="text-ink">aziende</strong> e <strong className="text-ink">aspiranti conducenti</strong>.
                                Noleggio e manutenzione defibrillatori AED. 4 sedi in Ticino.
                            </p>
                            <div className="mt-10 flex flex-col sm:flex-row gap-4">
                                <Link to="/corsi" className="btn-primary" data-testid="hero-cta-courses">
                                    Scopri i corsi <ArrowRight className="w-4 h-4" />
                                </Link>
                                <Link to="/richiesta-preventivo" className="btn-secondary" data-testid="hero-cta-quote">
                                    Richiedi preventivo
                                </Link>
                            </div>
                        </div>
                        <div className="lg:col-span-5 relative">
                            <div className="relative aspect-[4/5] overflow-hidden">
                                <img src={HERO_IMAGE} alt="Istruttrice 7MED che dimostra l'uso di un defibrillatore AED durante un corso BLS-AED in Ticino" className="w-full h-full object-cover" loading="eager" />
                            </div>
                        </div>
                    </div>
                    <div className="mt-12"><HeartbeatLine /></div>
                </div>
            </section>

            {/* STATS */}
            <section className="border-y border-ink/10 bg-white">
                <div className="container-7m grid grid-cols-2 lg:grid-cols-4 divide-x divide-ink/10">
                    {STATS.map((s) => (
                        <div key={s.label} className="py-6 sm:py-8 lg:py-12 px-3 sm:px-4 lg:px-8 first:pl-0 text-center lg:text-left">
                            <div className="font-heading font-black text-3xl sm:text-4xl lg:text-6xl tracking-tighter leading-none">{s.value}</div>
                            <div className="mt-2 text-[10px] sm:text-[11px] uppercase tracking-[0.18em] font-mono text-ink-muted">{s.label}</div>
                        </div>
                    ))}
                </div>
            </section>

            {/* CORSI — DUE CATEGORIE */}
            <section className="py-16 sm:py-20 lg:py-32 bg-white">
                <div className="container-7m">
                    <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 sm:gap-8 mb-10 sm:mb-14">
                        <div className="lg:col-span-6">
                            <div className="label-eyebrow text-brand">I nostri corsi</div>
                            <h2 className="mt-4 font-heading font-black text-4xl sm:text-5xl lg:text-6xl tracking-tighter leading-[1.02]">Quale corso<br />fa per te?</h2>
                        </div>
                        <div className="lg:col-span-5 lg:col-start-8 flex items-end">
                            <p className="text-readable-lg">
                                Due percorsi, quattro corsi: formazione obbligatoria per le aziende e per chi ottiene la patente di guida.
                            </p>
                        </div>
                    </div>

                    {/* Per le aziende */}
                    <div className="mb-12 lg:mb-16">
                        <div className="flex items-end justify-between mb-6 pb-4 border-b-2 border-ink">
                            <div className="flex items-center gap-4">
                                <div className="w-10 h-10 sm:w-12 sm:h-12 bg-brand text-white flex items-center justify-center"><Building2 className="w-5 h-5 sm:w-6 sm:h-6" strokeWidth={2} /></div>
                                <div>
                                    <div className="text-[11px] sm:text-[12px] font-mono uppercase tracking-[0.18em] text-brand">{CATEGORIES.aziende.label}</div>
                                    <h3 className="font-heading font-extrabold text-2xl sm:text-3xl tracking-tight leading-tight mt-0.5">Per le aziende</h3>
                                </div>
                            </div>
                            <div className="hidden sm:block text-xs font-mono text-ink-muted uppercase tracking-wider">3 corsi</div>
                        </div>
                        <div className="grid grid-cols-1 md:grid-cols-3 gap-px bg-ink/10">
                            {coursesByCategory("aziende").map((c, i) => (
                                <Link to={c.href} key={c.slug} className="group bg-white p-6 sm:p-8 hover:bg-ink hover:text-white transition-all duration-300 flex flex-col" data-testid={`home-course-${c.slug}`}>
                                    <div className="flex items-start justify-between mb-5">
                                        <c.menuIcon className="w-7 h-7 sm:w-8 sm:h-8 text-brand" strokeWidth={1.5} />
                                        <div className="font-mono text-xs tracking-[0.18em] text-ink-muted group-hover:text-white/60">0{i + 1}</div>
                                    </div>
                                    <h4 className="font-heading font-extrabold text-xl sm:text-2xl tracking-tight leading-[1.1]">{c.title}</h4>
                                    <p className="mt-2 text-[15px] leading-relaxed text-ink-muted group-hover:text-white/75">{c.subtitle}</p>
                                    <div className="mt-auto pt-5 border-t border-ink/10 group-hover:border-white/15 flex items-center justify-between">
                                        <div className="text-[11px] font-mono uppercase tracking-wider text-ink-muted group-hover:text-white/60">{c.duration}</div>
                                        <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
                                    </div>
                                </Link>
                            ))}
                        </div>
                    </div>

                    {/* Per la patente */}
                    <div>
                        <div className="flex items-end justify-between mb-6 pb-4 border-b-2 border-ink">
                            <div className="flex items-center gap-4">
                                <div className="w-10 h-10 sm:w-12 sm:h-12 bg-ink text-white flex items-center justify-center"><GraduationCap className="w-5 h-5 sm:w-6 sm:h-6" strokeWidth={2} /></div>
                                <div>
                                    <div className="text-[11px] sm:text-[12px] font-mono uppercase tracking-[0.18em] text-brand">{CATEGORIES.patente.label}</div>
                                    <h3 className="font-heading font-extrabold text-2xl sm:text-3xl tracking-tight leading-tight mt-0.5">Per la patente</h3>
                                </div>
                            </div>
                            <div className="hidden sm:block text-xs font-mono text-ink-muted uppercase tracking-wider">2 corsi</div>
                        </div>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-px bg-ink/10">
                            {coursesByCategory("patente").map((c, i) => (
                                <Link to={c.href} key={c.slug} className="group bg-white p-6 sm:p-8 hover:bg-ink hover:text-white transition-all duration-300 flex flex-col" data-testid={`home-course-${c.slug}`}>
                                    <div className="flex items-start justify-between mb-5">
                                        <c.menuIcon className="w-7 h-7 sm:w-8 sm:h-8 text-brand" strokeWidth={1.5} />
                                        <div className="font-mono text-xs tracking-[0.18em] text-ink-muted group-hover:text-white/60">0{i + 1}</div>
                                    </div>
                                    <h4 className="font-heading font-extrabold text-xl sm:text-2xl tracking-tight leading-[1.1]">{c.title}</h4>
                                    <p className="mt-2 text-[15px] leading-relaxed text-ink-muted group-hover:text-white/75">{c.subtitle}</p>
                                    <div className="mt-auto pt-5 border-t border-ink/10 group-hover:border-white/15 flex items-center justify-between">
                                        <div className="text-[11px] font-mono uppercase tracking-wider text-ink-muted group-hover:text-white/60">{c.duration} {c.price ? `· ${c.price}` : ""}</div>
                                        <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
                                    </div>
                                </Link>
                            ))}
                        </div>
                    </div>
                </div>
            </section>

            {/* DEFIBRILLATORI NOLEGGIO */}
            <section className="bg-ink text-white relative overflow-hidden">
                <div className="container-7m py-16 sm:py-20 lg:py-28">
                    <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-12 items-stretch">
                        <div className="lg:col-span-5 flex flex-col">
                            <div className="label-eyebrow !text-brand">Noleggio AED</div>
                            <h2 className="mt-4 font-heading font-black text-4xl sm:text-5xl lg:text-6xl tracking-tighter leading-[1.02]">Un AED quando serve.<br /><span className="text-brand">Senza acquisto.</span></h2>
                            <p className="mt-6 text-base sm:text-lg text-white/85 leading-relaxed max-w-md">
                                Defibrillatori certificati in <strong className="text-white">noleggio</strong> con manutenzione inclusa. Zero investimento iniziale, zero pensieri.
                            </p>
                            <div className="mt-8 grid grid-cols-2 gap-px bg-white/10">
                                {[
                                    { label: "Installazione", value: "Inclusa" },
                                    { label: "Manutenzione", value: "Programmata" },
                                    { label: "Consumabili", value: "Inclusi" },
                                    { label: "Formazione", value: "Base inclusa" },
                                ].map((it) => (
                                    <div key={it.label} className="bg-ink-soft p-4 sm:p-5">
                                        <div className="text-[10px] font-mono uppercase tracking-[0.18em] text-brand">{it.label}</div>
                                        <div className="mt-1 font-heading font-bold text-base sm:text-lg">{it.value}</div>
                                    </div>
                                ))}
                            </div>
                            <Link to="/defibrillatori-noleggio" className="btn-primary mt-8 self-start" data-testid="home-cta-aed">
                                Scopri il noleggio <ArrowRight className="w-4 h-4" />
                            </Link>
                        </div>

                        <div className="lg:col-span-7 relative">
                            <div className="aspect-[4/3] lg:aspect-auto lg:h-full overflow-hidden">
                                <img src="https://customer-assets.emergentagent.com/job_zealous-goodall-9/artifacts/3ncdwew1_shutterstock_2574472219.jpg" alt="Defibrillatore AED 7MED in noleggio per aziende e spazi pubblici" className="w-full h-full object-cover" loading="lazy" />
                            </div>
                            <div className="absolute top-4 right-4 sm:top-6 sm:right-6 bg-white text-ink p-4 sm:p-5 max-w-[180px] sm:max-w-[220px]">
                                <div className="text-[10px] font-mono uppercase tracking-[0.18em] text-brand">Sopravvivenza</div>
                                <div className="font-heading font-black text-3xl sm:text-4xl tracking-tighter mt-1">+70%</div>
                                <div className="text-[11px] sm:text-xs text-ink-muted leading-tight mt-1">con AED a portata entro 5 minuti</div>
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            {/* CLIENTI MARQUEE */}
            <section className="py-10 bg-white border-y border-ink/10 overflow-hidden">
                <div className="container-7m mb-4">
                    <div className="label-eyebrow">La fiducia di oltre 50 aziende e scuole</div>
                </div>
                <div className="marquee-track gap-12 lg:gap-20 whitespace-nowrap">
                    {[...CLIENTS, ...CLIENTS].map((c, i) => (
                        <div key={`${c}-${i}`} className="font-heading font-bold text-xl lg:text-3xl text-ink/20 hover:text-ink transition-colors">{c}</div>
                    ))}
                </div>
            </section>

            {/* PERCHÉ NOI */}
            <section className="py-16 sm:py-20 lg:py-28 bg-white">
                <div className="container-7m grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-12">
                    <div className="lg:col-span-5">
                        <div className="label-eyebrow text-brand">Perché 7MED</div>
                        <h2 className="mt-4 font-heading font-black text-3xl sm:text-4xl lg:text-5xl tracking-tighter leading-[1.1]">La fiducia si costruisce con i risultati.</h2>
                        <Link to="/chi-siamo" className="btn-secondary mt-8">Chi siamo</Link>
                    </div>
                    <div className="lg:col-span-7 grid grid-cols-1 sm:grid-cols-2 gap-px bg-ink/10">
                        {[
                            { icon: Award, title: "Istruttori Certificati", text: "Team con certificazioni SRC, abilitazioni USTRA e anni di esperienza." },
                            { icon: Activity, title: "+300 corsi all'anno", text: "Formazione continua, dai moduli brevi di refresh alle giornate intensive in azienda. Programmi concreti, tanta pratica, contenuti sempre aggiornati." },
                            { icon: Building2, title: "Da voi o da noi", text: "Veniamo direttamente presso la vostra sede oppure vi accogliamo nelle nostre aule a Mendrisio, Lugano, Bellinzona e Locarno." },
                            { icon: ShieldCheck, title: "Certificazioni ufficiali", text: "Tutti gli istruttori hanno grande esperienza, sia in qualità di formatore che sul campo. Siamo attestati SRC, USTRA e conformi alla normativa sul lavoro OLL3 Art. 36." },
                        ].map((b) => (
                            <div key={b.title} className="bg-white p-6 sm:p-8">
                                <b.icon className="w-7 h-7 text-brand" strokeWidth={1.5} />
                                <h3 className="mt-4 font-heading font-bold text-xl sm:text-xl tracking-tight">{b.title}</h3>
                                <p className="mt-2 text-[15px] sm:text-base text-ink-muted leading-[1.65]">{b.text}</p>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* SEDI */}
            <section className="py-16 sm:py-20 lg:py-28 bg-[#FAFAFA] border-y border-ink/10">
                <div className="container-7m">
                    <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 sm:gap-8 mb-10 sm:mb-12">
                        <div className="lg:col-span-6">
                            <div className="label-eyebrow text-brand">Le nostre sedi</div>
                            <h2 className="mt-4 font-heading font-black text-4xl sm:text-5xl lg:text-6xl tracking-tighter leading-[1.02]">4 sedi in Ticino.</h2>
                        </div>
                        <p className="lg:col-span-5 lg:col-start-8 text-readable-lg">
                            Corsi a Mendrisio, Lugano-Savosa, Bellinzona e Locarno. Su richiesta presso la vostra sede in tutta la Svizzera.
                        </p>
                    </div>
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-px bg-ink/10">
                        {SEDI.map((s) => (
                            <div key={s.slug} className="bg-white p-6 lg:p-8 hover:bg-brand hover:text-white transition-colors group">
                                <MapPin className="w-6 h-6 text-brand group-hover:text-white" strokeWidth={1.5} />
                                <h3 className="mt-4 font-heading font-bold text-xl sm:text-2xl">{s.city}</h3>
                                <div className="mt-2 text-[15px] text-ink-muted group-hover:text-white/90 leading-relaxed">
                                    <div>{s.address}</div>
                                    <div>{s.zip}</div>
                                    {s.cof && <div className="mt-1 text-xs italic">{s.cof}</div>}
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* FAQ */}
            <section className="py-16 sm:py-20 lg:py-28 bg-white">
                <div className="container-7m grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-12">
                    <div className="lg:col-span-4">
                        <div className="label-eyebrow text-brand">FAQ</div>
                        <h2 className="mt-4 font-heading font-black text-3xl sm:text-4xl lg:text-5xl tracking-tighter leading-[1.1]">Domande frequenti.</h2>
                        <p className="mt-5 text-readable">Tutte le risposte sui corsi, certificazioni e noleggio AED.</p>
                        <Link to="/contatti" className="btn-secondary mt-7">Contattaci</Link>
                    </div>
                    <div className="lg:col-span-7 lg:col-start-6">
                        <Accordion type="single" collapsible className="w-full">
                            {FAQ_HOME.map((f, i) => (
                                <AccordionItem key={f.q} value={`faq-${i}`} className="border-b border-ink/15" data-testid={`faq-item-${i}`}>
                                    <AccordionTrigger className="font-heading font-semibold text-base sm:text-lg lg:text-xl text-left hover:no-underline hover:text-brand py-5 sm:py-6 leading-snug">{f.q}</AccordionTrigger>
                                    <AccordionContent className="text-[15px] sm:text-base text-ink-muted leading-[1.7] pb-6">{f.a}</AccordionContent>
                                </AccordionItem>
                            ))}
                        </Accordion>
                    </div>
                </div>
            </section>

            {/* CTA */}
            <section className="bg-brand text-white">
                <div className="container-7m py-16 sm:py-20 lg:py-28 grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-12 items-center">
                    <div className="lg:col-span-7">
                        <div className="label-eyebrow !text-white/80">Pronto a partire?</div>
                        <h2 className="mt-4 font-heading font-black text-4xl sm:text-5xl lg:text-6xl tracking-tighter leading-[1.02]">Proteggi chi<br />lavora con te.</h2>
                        <p className="mt-5 sm:mt-6 text-base sm:text-lg text-white/85 max-w-xl leading-relaxed">Preventivo gratuito entro 24 ore. Senza impegno.</p>
                    </div>
                    <div className="lg:col-span-4 lg:col-start-9 space-y-4">
                        <Link to="/richiesta-preventivo" className="w-full inline-flex items-center justify-between bg-white text-ink px-7 py-5 font-bold uppercase tracking-wide text-sm hover:bg-ink hover:text-white transition-colors" data-testid="home-cta-final-quote">Richiedi preventivo <ArrowRight className="w-5 h-5" /></Link>
                        <a href={`tel:${COMPANY.phoneRaw}`} className="w-full inline-flex items-center justify-between border-2 border-white text-white px-7 py-5 font-bold uppercase tracking-wide text-sm hover:bg-white hover:text-brand transition-colors" data-testid="home-cta-final-phone">Chiamaci <Phone className="w-5 h-5" /></a>
                    </div>
                </div>
            </section>
        </>
    );
}
